import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { getComplaintList } from '@src/services/api';
import PageHeader from '@src/mobile/components/PageHeader';
import SharePanel from '@src/mobile/components/SharePanel';
import styles from './index.css';
import errorBoundary from '@ifeng-fe/errorBoundary';

const formatTime = (str) => {
    if (!str) return '';
    const d = new Date(str);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(
        2,
        '0'
    )}`;
};

const getDetailUrl = (cmpId) => {
    const path = window.location.pathname;
    const base = path.replace(/\/list\/?$/, '').replace(/\/$/, '');
    return `${window.location.origin}${base}/detail?cmp_id=${cmpId}`;
};
const LIST_SHARE_TITLE = '凤凰投诉';
const LIST_SHARE_DESC = '查看最新投诉与热门问题，欢迎来吐槽/点赞。';

const ListItem = ({ item }) => (
    <div
        className={styles.item}
        onClick={() => {
            window.location.href = getDetailUrl(item.cmp_id);
        }}>
        <div className={styles.itemMeta}>
            <div className={styles.nickname}>
                <span className={styles.nicknameLabel}>投诉对象：</span>
                <span className={styles.nicknameText}>{item.cmp_target || '-'}</span>
            </div>
            <span className={styles.itemTime}>{item.created_at}</span>
        </div>
        <div className={styles.itemRow}>
            <span className={styles.label}>昵&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;称：</span>
            <span className={styles.value}>{item.nickname}</span>
        </div>
        <div className={styles.itemRow}>
            <span className={styles.label}>诉求问题：</span>
            <span className={`${styles.value} ${styles.ellipsis1}`}>{item.cmp_issue}</span>
        </div>
        <div className={styles.itemRow}>
            <span className={styles.label}>投诉详情：</span>
            <span className={`${styles.value} ${styles.ellipsis10}`}>{item.cmp_detail}</span>
        </div>
    </div>
);

ListItem.propTypes = {
    item: PropTypes.shape({
        cmp_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        nickname: PropTypes.string,
        phone: PropTypes.string,
        cmp_target: PropTypes.string,
        cmp_issue: PropTypes.string,
        cmp_detail: PropTypes.string,
        created_at: PropTypes.string,
    }),
};

const Layout = ({ content }) => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [showBackTop, setShowBackTop] = useState(false);
    const [fixedRightVal, setFixedRightVal] = useState(0);
    const lastCmpIdRef = useRef(0);
    const containerRef = useRef(null);

    const loadMore = useCallback(async () => {
        if (loading || !hasMore) return;

        const fetchId = lastCmpIdRef.current;
        setLoading(true);
        try {
            const res = await getComplaintList(fetchId);
            const data = res?.data || res;
            const newList = data?.list || data?.items || [];
            const nextLastId = data?.last_cmp_id;

            setList((prev) => (fetchId === 0 ? newList : [...prev, ...newList]));
            if (nextLastId !== undefined && nextLastId !== null) {
                lastCmpIdRef.current = nextLastId;
            }
            setHasMore(newList.length > 0);
        } catch (err) {
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [loading, hasMore]);

    useEffect(() => {
        loadMore();
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const handler = () => {
            const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
            setShowBackTop(scrollTop > 300);
            if (scrollTop + clientHeight >= scrollHeight - 100 && hasMore && !loading) {
                loadMore();
            }
        };

        window.addEventListener('scroll', handler, { passive: true });
        return () => window.removeEventListener('scroll', handler);
    }, [hasMore, loading, loadMore]);

    return (
        <div className={styles.layout} ref={containerRef}>
            <SharePanel title={LIST_SHARE_TITLE} desc={LIST_SHARE_DESC} url={window.location.href} />
            <PageHeader showFormEntry variant="banner" desc="聚焦消费者权益保护，等你来发声" setFixedRightVal={setFixedRightVal} />

            <div className={styles.list}>
                {list.map((item) => (
                    <ListItem key={item.cmp_id} item={item} />
                ))}
            </div>

            {loading && <div className={styles.loading}>加载中...</div>}

            {!hasMore && list.length > 0 && <div className={styles.noMore}>没有更多了</div>}

            {!loading && list.length === 0 && <div className={styles.empty}>暂无数据</div>}

            {showBackTop && (
                <button
                    type="button"
                    className={styles.backTopEntry}
                    style={{ right: `${fixedRightVal + 20}px` }}
                    aria-label="回到顶部"
                    onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}>
                    <span className={styles.backTopIcon} />
                </button>
            )}
        </div>
    );
};

Layout.propTypes = {
    content: PropTypes.object,
};

export default errorBoundary(Layout);
